import React, { useContext, useEffect, useRef, useState } from "react"
import gsap from "gsap"

import useMousePosition from "../../hooks/mouse_poisition"
import { CursorContext } from "../../context/cursor/context"
import { ThemeContext } from "../../context/theme/context"
import isTouchDevice from "../../utils/is_touch_device"

const Cursor = () => {
    const [cursor] = useContext(CursorContext)
    const { state } = useContext(ThemeContext)

    const { clientX, clientY } = useMousePosition()
    const [isVisible, setIsVisible] = useState(false)

    const cursorRef = useRef(null)
    const followerRef = useRef(null)
    const particlesRef = useRef([])

    useEffect(() => {
        // Only enable custom cursor for non-touch devices
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
            if (cursorRef.current) cursorRef.current.style.display = 'none'
            if (followerRef.current) followerRef.current.style.display = 'none'
            return
        }

        const ctx = gsap.context(() => {
            let mouseX = 0
            let mouseY = 0
            let prevMouseX = 0
            let prevMouseY = 0
            let lastParticleTime = 0
            const particleInterval = 150 // ms between particle spawns for better performance

            // Update mouse position
            const updateMousePosition = (e) => {
                mouseX = e.clientX
                mouseY = e.clientY
            }

            // Fast-moving cursor (triangle)
            const updateCursor = () => {
                gsap.set(cursorRef.current, {
                    x: mouseX,
                    y: mouseY,
                })
            }

            // Performance optimization - throttle particle creation
            const createParticle = () => {
                const now = Date.now()
                // Calculate mouse speed (used to determine if particles should spawn)
                const mouseSpeed = Math.sqrt(
                    Math.pow(mouseX - prevMouseX, 2) +
                    Math.pow(mouseY - prevMouseY, 2)
                )

                // Only create particles if mouse is moving and enough time has passed
                if (mouseSpeed > 5 && now - lastParticleTime > particleInterval) {
                    const particle = document.createElement('div')
                    particle.className = 'cursor-particle'
                    document.body.appendChild(particle)
                    particlesRef.current.push(particle)

                    gsap.set(particle, {
                        x: mouseX,
                        y: mouseY,
                        scale: gsap.utils.random(0.3, 0.8)
                    })

                    gsap.to(particle, {
                        y: mouseY + gsap.utils.random(-20, 20),
                        x: mouseX + gsap.utils.random(-20, 20),
                        opacity: 0,
                        scale: 0,
                        duration: gsap.utils.random(0.5, 1),
                        ease: "power1.out",
                        onComplete: () => {
                            if (particle && particle.parentNode) {
                                particle.remove()
                            }
                            particlesRef.current = particlesRef.current.filter(p => p !== particle)
                        }
                    })

                    lastParticleTime = now
                }

                prevMouseX = mouseX
                prevMouseY = mouseY
            }

            // Follower with magnetic effect
            const updateFollower = () => {
                const interactiveElements = document.querySelectorAll('a, button, [data-magnetic], .MuiButtonBase-root')

                // Use a weakmap to store element transformations
                const elementTransforms = new WeakMap()

                let isHovering = false
                interactiveElements.forEach(element => {
                    const rect = element.getBoundingClientRect()
                    const centerX = rect.left + rect.width / 2
                    const centerY = rect.top + rect.height / 2
                    const distance = Math.sqrt(
                        Math.pow(mouseX - centerX, 2) +
                        Math.pow(mouseY - centerY, 2)
                    )

                    if (distance < 100) {
                        isHovering = true
                        const strength = (100 - distance) / 100
                        gsap.to(followerRef.current, {
                            x: centerX + (mouseX - centerX) * strength,
                            y: centerY + (mouseY - centerY) * strength,
                            scale: 2,
                            duration: 0.3,
                            ease: "power2.out"
                        })

                        // Only create a new tween if this element doesn't have one
                        if (!elementTransforms.has(element)) {
                            const tween = gsap.to(element, {
                                x: (mouseX - centerX) * 0.4 * strength,
                                y: (mouseY - centerY) * 0.4 * strength,
                                duration: 0.3,
                                ease: "power2.out"
                            })
                            elementTransforms.set(element, tween)
                        } else {
                            // Update existing tween
                            const tween = elementTransforms.get(element)
                            tween.vars.x = (mouseX - centerX) * 0.4 * strength
                            tween.vars.y = (mouseY - centerY) * 0.4 * strength
                            tween.invalidate().restart()
                        }
                    } else if (elementTransforms.has(element)) {
                        // Reset element position if it's no longer being hovered
                        gsap.to(element, {
                            x: 0,
                            y: 0,
                            duration: 0.3,
                            ease: "power2.out",
                            onComplete: () => {
                                elementTransforms.delete(element)
                            }
                        })
                    }
                })

                if (!isHovering) {
                    gsap.to(followerRef.current, {
                        x: mouseX,
                        y: mouseY,
                        scale: 1,
                        duration: 0.3,
                        ease: "power2.out"
                    })
                }

                // Create particles (throttled)
                createParticle()
            }

            // Use requestAnimationFrame for more efficient cursor updates
            const animateCursor = () => {
                updateCursor()
                requestAnimationFrame(animateCursor)
            }
            requestAnimationFrame(animateCursor)

            // Use GSAP ticker for follower and effects (runs at optimal frame rate)
            gsap.ticker.add(updateFollower)
            window.addEventListener('mousemove', updateMousePosition, { passive: true })

            // Initial appearance
            gsap.from([cursorRef.current, followerRef.current], {
                scale: 0,
                duration: 0.5,
                ease: "elastic.out(1, 0.3)",
                stagger: 0.1
            })

            return () => {
                window.removeEventListener('mousemove', updateMousePosition)
                gsap.ticker.remove(updateFollower)
                particlesRef.current.forEach(particle => {
                    if (particle && particle.parentNode) {
                        particle.remove()
                    }
                })
            }
        })

        const handleMouseEnter = () => setIsVisible(true)
        const handleMouseLeave = () => setIsVisible(false)

        document.body.addEventListener("mouseenter", handleMouseEnter)
        document.body.addEventListener("mouseleave", handleMouseLeave)

        return () => {
            document.body.removeEventListener("mouseenter", handleMouseEnter)
            document.body.removeEventListener("mouseleave", handleMouseLeave)

            ctx.revert()
        }
    }, [])

    if (isTouchDevice) return null

    return (
        <>
            <div
                ref={cursorRef}
                aria-hidden="true"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    zIndex: 9999,
                    pointerEvents: "none",
                    mixBlendMode: "difference",
                    transform: "translate(-50%, -50%)",
                }}
            >
                <svg
                    width={50}
                    height={50}
                    viewBox="0 0 50 50"
                    style={{
                        position: "absolute",
                        transform: `translate(-50%, -50%) scale(${cursor.active ? 40 : 4})`,
                        fill: state.color.secondary,
                        transition: "transform .2s ease-in-out",
                        opacity: isVisible && clientX > 1 ? 1 : 0,
                    }}>
                    <circle
                        cx="25"
                        cy="25"
                        r="8"
                    />
                </svg>
            </div>
            <div
                ref={followerRef}
                aria-hidden="true"
                style={{
                    position: "absolute",
                    left: clientX,
                    top: clientY,
                    pointerEvents: "none",
                    transform: "translate(-50%, -50%)",
                    zIndex: 9998,
                    mixBlendMode: "difference",
                }}
            />
        </>
    )
}

export default Cursor